import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import { useSaveContactFormMutation } from "../../services/apps";
import "./contact.scss";
import useScreenSize from "../../hooks/useScreenSize";


type FormState = {
  name: string;
  email: string;
  mobile: string;
  query: string;
};

type ErrorState = {
  name?: string;
  email?: string;
  query?: string;
};

export default function Contact() {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    mobile: "",
    query: ""
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const [submitted, setSubmitted] = useState(false);
  const [saveContactForm, { isLoading, isError, isSuccess, error }] = useSaveContactFormMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = (): ErrorState => {
    const newErrors: ErrorState = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) newErrors.email = "Invalid email";
    if (!form.query.trim()) newErrors.query = "Query is required";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      try {
        await saveContactForm(form).unwrap();
        setSubmitted(true);
        setForm({ name: "", email: "", mobile: "", query: "" });
      } catch (err) {
        // error handled by isError
      }
    }
  };

  const handleLogin = () => {
    navigate(ROUTES.PATHS.LOGIN);
  };

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="privacy-page">
      <header>
        <div className="container">
          <nav>
            <a className="logo" href="/">
              <span className="logo-dot"></span>
              <strong>SerialMan</strong>
              {!isMobile && <span style={{ opacity: 0.6, fontSize: "0.9rem" }}>Queue & Appointments</span>}
            </a>
            <div className="links">
              {!isMobile && <a className="link" href="/#features">Features</a>}
              {!isMobile && <a className="link" href="/#pricing">Pricing</a>}
              <a className="link" href="/privacy-policy">Privacy</a>
              <a className="link" href="/terms">Terms</a>
              <a className="link" onClick={handleLogin}>Login</a>
            </div>
          </nav>
        </div>
      </header>

      <section className="hero-section">
        <div className="container">
          <h1>Contact Us</h1>
          <p className="lead">We'd love to hear from you. Please fill out the form below and we'll get back to you soon.</p>
        </div>
      </section>

      <div className="container policy-container">
        <main className="policy-content">
          <section className="policy-section">
            {submitted ? (
              <div className="contact-success">
                <h2>Thank you!</h2>
                <p>Your query has been submitted successfully.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="name">Name<span style={{ color: "#e74c3c" }}>*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={errors.name ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email<span style={{ color: "#e74c3c" }}>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={errors.email ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.email && <div className="form-error">{errors.email}</div>}
                </div>
                <div className="form-group">
                  <label htmlFor="mobile">Mobile No.</label>
                  <input
                    type="text"
                    id="mobile"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="query">Query<span style={{ color: "#e74c3c" }}>*</span></label>
                  <textarea
                    id="query"
                    name="query"
                    value={form.query}
                    onChange={handleChange}
                    className={errors.query ? "error" : ""}
                    rows={5}
                    disabled={isLoading}
                  />
                  {errors.query && <div className="form-error">{errors.query}</div>}
                </div>
                {isError && <div className="form-error" style={{ marginBottom: 10 }}>Submission failed. Please try again.</div>}
                <button type="submit" className="btn btn-primary" disabled={isLoading}>{isLoading ? "Submitting..." : "Submit"}</button>
              </form>
            )}
          </section>
        </main>
      </div>

      <footer>
        <div className="container" style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "30px 0" }}>
          <div className="copyright">© 2026 HrnSoft. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
